/**
 * Import helpers for bringing existing Excalidraw files into the app
 *
 * The pure, framework-free logic behind the "Import files" starting point of
 * the new-diagram dialog (task 5.5, Requirement 16). Kept out of the component
 * so the file-name-to-title rule and the file reader can be unit-tested, and so
 * the accepted-extension list is defined in exactly one place.
 *
 * `readImportFile` wraps Excalidraw's `loadSceneOrLibraryFromBlob`, which reads
 * both `.excalidraw` scene JSON and the scene embedded in an Excalidraw-exported
 * `.svg`/`.png`. Like `exportDiagramSvg` in `diagramSvg.ts`, it imports
 * `@excalidraw/excalidraw` dynamically, so importing this module (for the title
 * helper or the extension list) does not pull the heavy Excalidraw bundle into
 * the main chunk; the package loads only when an import actually starts
 * (Requirement 1.4, task note "import Excalidraw dynamically, like
 * ExcalidrawCanvas").
 */

// Must run before Excalidraw loads: points its font loader at our own origin.
import "@/components/diagrams/excalidrawAssetPath"
import type { DiagramScene } from "@/components/diagrams/diagramSvg"

/**
 * The file extensions the importer accepts (Requirement 16.1):
 *   - `.excalidraw` — Excalidraw scene JSON;
 *   - `.svg` / `.png` — exported from Excalidraw with the scene embedded
 *     (for example `.excalidraw.svg`, `.excalidraw.png`).
 *
 * Lowercase and dot-prefixed, so they can be compared against a lowercased file
 * name's suffix and passed to `react-dropzone`'s `accept` option by extension.
 */
export const ACCEPTED_IMPORT_EXTENSIONS: readonly string[] = [
  ".excalidraw",
  ".svg",
  ".png",
  // Accepted only so a library reaches `readImportFile` and gets the specific
  // "this is an icon library" rejection rather than a generic one (Req 16.2).
  ".excalidrawlib",
]

/**
 * The reason a file was rejected by the importer, as a machine-readable tag so
 * the UI can show the fixed message from Requirement 16.2 without matching on
 * prose.
 *
 *   - `"library"` — the file is an Excalidraw icon library, not a diagram;
 *   - `"no-scene"` — the file carries no Excalidraw scene (e.g. a plain PNG).
 */
export type ImportRejectionReason = "library" | "no-scene"

/**
 * The user-facing message for each rejection reason, worded exactly as
 * Requirement 16.2 specifies.
 */
export const IMPORT_REJECTION_MESSAGES: Readonly<Record<ImportRejectionReason, string>> = {
  library:
    "This is an icon library, not a diagram. Libraries are added to the diagrams repo.",
  "no-scene": "This file doesn't contain an Excalidraw scene.",
}

/**
 * A typed rejection thrown by `readImportFile` when a file is not an importable
 * diagram. Carries the `reason` tag so the caller can list the file with the
 * matching fixed message and continue past it (Requirement 16.2, 16.5).
 */
export class ImportRejectedError extends Error {
  /** Why the file was rejected, for selecting the fixed user-facing message. */
  public readonly reason: ImportRejectionReason

  /**
   * Construct an ImportRejectedError.
   *
   * @param params - The rejection details.
   * @param params.reason - The machine-readable rejection reason.
   */
  constructor({ reason }: { reason: ImportRejectionReason }) {
    super(IMPORT_REJECTION_MESSAGES[reason])
    this.name = "ImportRejectedError"
    this.reason = reason
    // Restore the prototype chain so `instanceof ImportRejectedError` works
    // after TypeScript downlevels the class (the usual Error-subclass quirk).
    Object.setPrototypeOf(this, ImportRejectedError.prototype)
  }
}

/**
 * The longest accepted extension chain stripped from a file name when deriving
 * a default title: any trailing run of the accepted extensions, in any order
 * and repetition (so `.excalidraw.png`, `.excalidraw.svg`, and a bare `.png`
 * all reduce to the base name). Built from {@link ACCEPTED_IMPORT_EXTENSIONS}
 * so the two never drift apart.
 *
 * Each extension is escaped for use in a character-class-free alternation; the
 * only regex metacharacter present is the leading dot, escaped to `\.`.
 */
const TRAILING_EXTENSIONS_PATTERN: RegExp = (() => {
  const alternation: string = ACCEPTED_IMPORT_EXTENSIONS.map(
    (extension: string): string => extension.replace(/\./g, "\\."),
  ).join("|")
  // One or more of the accepted extensions, anchored to the end of the string.
  return new RegExp(`(?:${alternation})+$`, "i")
})()

/**
 * Derive a default Diagram_Title from an imported file's name (Requirement
 * 16.3): strip the accepted extensions (`.excalidraw`, `.svg`, `.png`, and
 * combinations such as `.excalidraw.png`), replace `-` and `_` with spaces,
 * collapse the surrounding whitespace, and capitalise the first letter.
 *
 * The result is a suggestion the user can edit in the dialog; it is not
 * validated here (the dialog validates the final title at the trust boundary).
 * A name that is nothing but extensions, or empty, yields an empty string so
 * the caller can fall back or prompt rather than invent a title.
 *
 * @param fileName - The original file name, e.g. "oxpecker-arch.excalidraw.png".
 * @returns The derived title, e.g. "Oxpecker arch", or "" when none remains.
 */
export function titleFromFileName(fileName: string): string {
  // Drop any leading directory parts a drag-and-drop may include, then the
  // trailing accepted-extension chain.
  const baseName: string = fileName.split(/[\\/]/).pop() ?? fileName
  const withoutExtensions: string = baseName.replace(TRAILING_EXTENSIONS_PATTERN, "")

  // `-` and `_` become spaces; then collapse runs of whitespace and trim, so
  // "oxpecker__arch" and "oxpecker  arch" both become "Oxpecker arch".
  const spaced: string = withoutExtensions
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (spaced === "") {
    return ""
  }

  // Capitalise only the first letter; the rest of the name is left as typed so
  // existing capitalisation (e.g. "DuckDB") is preserved.
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Read one imported file into an Excalidraw scene ready to export as a
 * Diagram_File.
 *
 * Wraps Excalidraw's `loadSceneOrLibraryFromBlob` (dynamically imported) and
 * maps its two non-diagram outcomes to the typed rejections of Requirement
 * 16.2:
 *   - an Excalidraw *library* file → {@link ImportRejectedError} `"library"`;
 *   - a file with *no scene* (for example a plain PNG, which parses but yields
 *     no elements) → {@link ImportRejectedError} `"no-scene"`.
 *
 * The returned scene keeps the file's own elements, embedded images, and canvas
 * app state; the fixed export settings (white background, embedded scene) are
 * applied later by `exportDiagramSvg`, not here (Requirement 16.4).
 *
 * @param file - The file chosen by the user (picker or drag and drop).
 * @returns The imported Excalidraw scene.
 * @throws ImportRejectedError if the file is a library or carries no scene.
 * @throws Error if Excalidraw cannot parse the file at all (a corrupt file).
 */
export async function readImportFile(file: File): Promise<DiagramScene> {
  // Dynamic import keeps Excalidraw out of the main bundle, matching
  // ExcalidrawCanvas and exportDiagramSvg; it loads only when an import runs.
  const { loadSceneOrLibraryFromBlob } = await import("@excalidraw/excalidraw")

  // No local app state or elements to merge onto: this is a fresh import, not a
  // drop into an open scene. Excalidraw throws, rather than returning an empty
  // scene, for an image with no embedded scene (an `ImageSceneDataError`, whose
  // `name` is "EncodingError") and for a file it does not recognise ("Error:
  // invalid file"); both mean "no scene" (Requirement 16.2).
  let loaded: Awaited<ReturnType<typeof loadSceneOrLibraryFromBlob>>
  try {
    loaded = await loadSceneOrLibraryFromBlob(file, null, null)
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      (error.name === "EncodingError" || error.message === "Error: invalid file")
    ) {
      throw new ImportRejectedError({ reason: "no-scene" })
    }
    throw error
  }

  // An Excalidraw library (.excalidrawlib, or a library embedded in an export)
  // is not a diagram; reject it with the library message (Requirement 16.2).
  if (loaded.type === "application/vnd.excalidrawlib+json") {
    throw new ImportRejectedError({ reason: "library" })
  }

  const restored = loaded.data

  // A file Excalidraw can open but that holds no scene (for example a plain PNG
  // with no embedded scene) restores to an empty element list. Treat that as
  // "no scene" rather than importing a blank diagram (Requirement 16.2).
  const hasElements: boolean = restored.elements.some(
    (element): boolean => element.isDeleted !== true,
  )
  if (!hasElements) {
    throw new ImportRejectedError({ reason: "no-scene" })
  }

  return {
    elements: restored.elements,
    appState: restored.appState,
    files: restored.files,
  }
}
